import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";
import styled from "styled-components";
import { formatDateShort } from "../utils";
import { InspectionInfoBody } from "./InspectionForm";
import { InspectionForm_data$key } from "./__generated__/InspectionForm_data.graphql";

const InspectionFormDataContainer = (props: {
  inspection: InspectionForm_data$key;
}) => {
  const data = useFragment(
    graphql`
      fragment InspectionForm_data on InspectionNode {
        id
        inspectionfield {
          id
          externalId
          plannedDate
          inspector {
            firstName
            lastName
            user {
              firstName
              lastName
            }
          }
          managementPackageVariant {
            name
            code
            managementPackage {
              name
            }
          }
          periodStart
          periodEnd
          member {
            id
            name
            email
            phone
            company {
              address {
                edges {
                  node {
                    street
                    houseNumber
                    zipcode
                    city
                  }
                }
              }
            }
          }
          field {
            periodStart
            periodEnd
          }
        }
      }
    `,
    props.inspection
  );
  const managementPackageVariantName = `${
    data.inspectionfield?.managementPackageVariant?.code || ""
  }, ${
    data.inspectionfield?.managementPackageVariant?.managementPackage?.name
      ? `${data.inspectionfield?.managementPackageVariant?.managementPackage?.name}, ${data.inspectionfield?.managementPackageVariant?.name}`
      : data.inspectionfield?.managementPackageVariant?.name
  }`;
  const inspector = data?.inspectionfield?.inspector;
  const inspectorName = `${
    inspector?.user?.firstName || inspector?.firstName
  } ${inspector?.user?.lastName || inspector?.lastName}`;

  return (
    <InspectionInfoBody>
      <InspectionBaseData
        member={{
          name: data?.inspectionfield?.member?.name,
          email: data?.inspectionfield?.member?.email,
          mobilePhone: data?.inspectionfield?.member?.phone,
          phone: data?.inspectionfield?.member?.phone,
          address: {
            street:
              data?.inspectionfield?.member?.company?.address.edges[0]?.node
                ?.street || null,
            houseNumber:
              data?.inspectionfield?.member?.company?.address.edges[0]?.node
                ?.houseNumber || null,
            zipcode:
              data?.inspectionfield?.member?.company?.address.edges[0]?.node
                ?.zipcode || null,
            city:
              data?.inspectionfield?.member?.company?.address.edges[0]?.node
                ?.city || null,
          },
        }}
        field={{
          id: data?.inspectionfield?.externalId?.toString(),
          projectName: managementPackageVariantName,
          periodStart: data?.inspectionfield?.field?.periodStart,
          periodEnd: data?.inspectionfield?.field?.periodEnd,
        }}
        plannedDate={data?.inspectionfield?.plannedDate}
        inspectorName={inspectorName}
      />
    </InspectionInfoBody>
  );
};

type Nullable<T> = T | null;
export const InspectionBaseData = (props: {
  member?: Nullable<{
    name?: Nullable<string>;
    email?: Nullable<string>;
    address?: Nullable<{
      street: Nullable<string>;
      houseNumber: Nullable<number>;
      zipcode: Nullable<string>;
      city: Nullable<string>;
    }>;
    mobilePhone?: Nullable<string>;
    phone?: Nullable<string>;
  }>;
  plannedDate?: Nullable<string>;
  inspectorName?: Nullable<string>;
  field?: Nullable<{
    id?: Nullable<string>;
    projectName?: Nullable<string>;
    periodStart?: Nullable<string>;
    periodEnd?: Nullable<string>;
  }>;
}) => {
  return (
    <BaseDataDiv>
      <BaseDataH4>Basisgegevens</BaseDataH4>

      <>
        <HeaderText>Deelnemer</HeaderText>
        <table>
          <tbody>
            <DataRow>
              <DataDescription>Naam</DataDescription>
              <DataValue>{props.member?.name}</DataValue>
            </DataRow>
            <DataRow>
              <DataDescription>E-mailadres</DataDescription>
              <DataValue>{props.member?.email}</DataValue>
            </DataRow>
            <DataRow>
              <DataDescription>Adres</DataDescription>
              <DataValue>
                {props.member?.address?.street}{" "}
                {props.member?.address?.houseNumber}
              </DataValue>
            </DataRow>
            <DataRow>
              <DataDescription>Postcode en woonplaats</DataDescription>
              <DataValue>
                {props.member?.address?.zipcode} {props.member?.address?.city}
              </DataValue>
            </DataRow>
            <DataRow>
              <DataDescription>Mobiele nummer</DataDescription>
              <DataValue>{props.member?.mobilePhone}</DataValue>
            </DataRow>
            <DataRow>
              <DataDescription>Vast nummer</DataDescription>
              <DataValue>{props.member?.phone}</DataValue>
            </DataRow>
          </tbody>
        </table>
      </>
      <>
        <HeaderText>Schouw</HeaderText>
        <table>
          <tbody>
            <DataRow>
              <DataDescription>Datum uitvoering schouw</DataDescription>
              <DataValue>
                {formatDateShort(props.plannedDate || undefined)}
              </DataValue>
            </DataRow>
            <DataRow>
              <DataDescription>Schouwer</DataDescription>
              <DataValue>{props.inspectorName}</DataValue>
            </DataRow>
            <DataRow>
              <DataDescription>Beheereenheid</DataDescription>
              <DataValue>{props.field?.id || ""}</DataValue>
            </DataRow>
            <DataRow>
              <DataDescription>Beheerpakket</DataDescription>
              <DataValue>{props.field?.projectName || ""}</DataValue>
            </DataRow>
            <DataRow>
              <DataDescription>Looptijd</DataDescription>
              <DataValue>
                {formatDateShort(props.field?.periodStart || undefined)} -{" "}
                {formatDateShort(props.field?.periodEnd || undefined)}
              </DataValue>
            </DataRow>
          </tbody>
        </table>
      </>
    </BaseDataDiv>
  );
};

const BaseDataDiv = styled.div`
  padding-top: 32px;
  padding-bottom: 80px;
  padding-left: 62px;
`;

const BaseDataH4 = styled.h4`
  /* Basisgegevens */

  /* H4 */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 120%;

  /* identical to box height, or 22px */
  display: flex;
  align-items: center;

  /* Donkergrijs */
  color: #212529;
`;

const HeaderText = styled.div`
  margin-top: 41px;
  margin-bottom: 17px;
  /* Deelnemer */

  /* Text small */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 120%;

  /* identical to box height, or 16px */
  display: flex;
  align-items: center;

  /* BoerenNatuur Blauw */
  color: #0075bc;
`;

const DataRow = styled.tr`
  font-size: 14px;
  height: 2.2em;
`;

const DataDescription = styled.th`
  /* Text small - bold */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 120%;
  text-align: left;

  /* or 14px */

  /* Donkergrijs */
  color: #212529;
`;

const DataValue = styled.td`
  padding-left: 20px;
  /* Text small */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 120%;

  /* or 14px */

  /* Donkergrijs */
  color: #212529;
`;
export default InspectionFormDataContainer;
